import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, errText, photoUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import Cropper from "react-easy-crop";
import { Slider } from "../components/ui/slider";
import { PlacementLink } from "../components/PlacementLink";
import { BannerMockup } from "../components/BannerMockup";
import { useLanguage } from "../context/LanguageContext";
import { ADVERTISE_COPY, PLACEMENT_PREVIEW_IMG } from "../lib/advertiseCopy";
import {
  Megaphone, ImagePlus, Loader2, Trash2, Download, Eye, MousePointerClick, ShieldCheck,
  BadgeCheck, Mail, CheckCircle2, Calendar, RefreshCw, Activity, Ticket, BellRing, AlertTriangle,
  ZoomIn, ZoomOut, Crop, Layers, X, Sparkles, Plus,
} from "lucide-react";
import {
  PremiumPill, PremiumPerks, PLACEMENT_VALUES, IDEAL_SIZE, STATUS_CLS, fmtDate,
  emptyForm, LOAD_CLS, PLACEMENT_RATIO, IDEAL_PX, getCroppedBlob,
} from "./advertise/advertiseHelpers";

export default function Advertise() {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const tt = ADVERTISE_COPY[lang] || ADVERTISE_COPY.en;
  const location = useLocation();
  const [pricing, setPricing] = useState(null);
  const [slots, setSlots] = useState(null);
  const [myAds, setMyAds] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropPlacement, setCropPlacement] = useState("home");
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [renewAd, setRenewAd] = useState(null);
  const [renewDays, setRenewDays] = useState(7);
  const [renewing, setRenewing] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [previewPlacement, setPreviewPlacement] = useState(null);
  const [showMine, setShowMine] = useState(false);
  const [bannerDims, setBannerDims] = useState(null);
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [digestOn, setDigestOn] = useState(true);
  const [bundleSlots, setBundleSlots] = useState([]);
  const [comboMode, setComboMode] = useState(false);
  const startBundle = (slots, combo = false) => { setBundleSlots(slots); setComboMode(combo); };

  const eligible = user && (user.role === "admin" || user.is_verified_seller || user.verified_seller || user.verified_business);

  const loadAds = () => api.get("/my/ads").then((r) => setMyAds(r.data)).catch(() => setMyAds([]));
  const loadSlots = () => api.get("/ads/slots").then((r) => setSlots(r.data)).catch(() => {});
  useEffect(() => {
    if (!eligible) return;
    api.get("/ads/pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/ads/digest-preference").then((r) => setDigestOn(r.data.enabled)).catch(() => {});
    loadAds();
    loadSlots();
  }, [eligible]);

  useEffect(() => { setBannerDims(null); }, [form.image]);

  // "Book this banner" / bundle — carry a previewed banner + slot(s) from the placements page
  useEffect(() => {
    if (!eligible) return;
    const params = new URLSearchParams(location.search);
    const banner = params.get("banner");
    const placement = params.get("placement");
    const slots = (params.get("slots") || "").split(",").map((s) => s.trim()).filter((s) => PLACEMENT_VALUES.includes(s));
    const uniqueSlots = [...new Set(slots)];
    if (uniqueSlots.length >= 2) {
      setForm((f) => ({ ...f, image: banner || f.image, placement: uniqueSlots[0] }));
      setBundleSlots(uniqueSlots);
      toast.success(tt.t_bundle_loaded(uniqueSlots.length));
      window.history.replaceState({}, "", "/advertise");
    } else if (banner) {
      setForm((f) => ({ ...f, image: banner, placement: PLACEMENT_VALUES.includes(placement) ? placement : f.placement }));
      toast.success(tt.t_banner_loaded);
      window.history.replaceState({}, "", "/advertise");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible]);

  const trackPlacement = (placement, action) => { api.post("/ads/track-placement", { placement, action }).catch(() => {}); };
  const openPreview = (placement) => { setShowMine(!!form.image); setPreviewPlacement(placement); trackPlacement(placement, "preview"); };

  const applyCoupon = async () => {
    if (!coupon.trim()) { setCouponInfo(null); return; }
    setCheckingCoupon(true);
    try {
      const { data } = await api.post("/ads/validate-coupon", { code: coupon.trim(), days: Number(form.days) || 7, placement: form.placement });
      if (data.valid) { setCouponInfo(data); toast.success(tt.t_code_applied(data.discount)); }
      else { setCouponInfo(null); toast.error(tt.t_code_invalid); }
    } catch (e) { setCouponInfo(null); toast.error(errText(e)); }
    finally { setCheckingCoupon(false); }
  };

  const toggleDigest = async (v) => {
    setDigestOn(v);
    try { await api.put("/ads/digest-preference", { enabled: v }); toast.success(v ? tt.t_weekly_on : tt.t_weekly_off); }
    catch (e) { toast.error(errText(e)); setDigestOn(!v); }
  };

  const onImage = (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error(tt.t_upload_img);
    const reader = new FileReader();
    reader.onload = () => { setCropSrc(reader.result); setCropPlacement(form.placement); setCrop({ x: 0, y: 0 }); setZoom(1); setShowCropper(true); };
    reader.readAsDataURL(file);
  };

  const openRecrop = () => { if (!cropSrc) return; setCropPlacement(form.placement); setCrop({ x: 0, y: 0 }); setZoom(1); setShowCropper(true); };

  const applyCrop = async () => {
    if (!croppedAreaPixels) return;
    setUploading(true);
    try {
      const [ow, oh] = IDEAL_PX[cropPlacement] || IDEAL_PX.home;
      const blob = await getCroppedBlob(cropSrc, croppedAreaPixels, ow, oh);
      const fd = new FormData();
      fd.append("file", new File([blob], "ad-banner.jpg", { type: "image/jpeg" }));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.path }));
      setShowCropper(false);
    } catch (err) { toast.error(errText(err)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.image) return toast.error(tt.t_upload_first);
    if (!form.link_url.trim()) return toast.error(tt.t_add_link);
    setSubmitting(true);
    try {
      const endpoint = bundleMode ? "/ads/submit-bundle" : "/ads/submit";
      const body = bundleMode
        ? { title: form.title.trim(), image: form.image, link_url: form.link_url.trim(),
            placements: bundleSlots, days: Number(form.days) || 7, combo: comboMode,
            start_at: form.start_at ? new Date(form.start_at).toISOString() : "",
            coupon: couponInfo?.valid ? coupon.trim() : "" }
        : { title: form.title.trim(), image: form.image, link_url: form.link_url.trim(),
            placement: form.placement, days: Number(form.days) || 7,
            start_at: form.start_at ? new Date(form.start_at).toISOString() : "",
            coupon: couponInfo?.valid ? coupon.trim() : "" };
      const { data } = await api.post(endpoint, body);
      setReceipt(data);
      setForm(emptyForm);
      setBundleSlots([]); setComboMode(false);
      setCoupon(""); setCouponInfo(null);
      loadAds();
      loadSlots();
    } catch (e) { toast.error(errText(e)); }
    finally { setSubmitting(false); }
  };

  const doRenew = async () => {
    if (!renewAd) return;
    setRenewing(true);
    try {
      const { data } = await api.post(`/ads/${renewAd.id}/renew`, { days: Number(renewDays) || 7 });
      setReceipt(data);
      setRenewAd(null);
      loadAds();
      loadSlots();
      toast.success(tt.t_renewal_booked);
    } catch (e) { toast.error(errText(e)); }
    finally { setRenewing(false); }
  };

  const downloadReport = async (ad) => {
    try {
      const res = await api.get(`/my/ads/${ad.id}/report.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `ad-report-${ad.id.slice(0, 8)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errText(e)); }
  };

  const priceFor = (days, placement = form.placement) => {
    if (!pricing) return { amount: null, discount: 0, rate: null };
    const rate = (pricing.rates && pricing.rates[placement]) || pricing.daily_rate;
    const d = Math.max(1, Math.min(Number(days) || 0, 90));
    let disc = 0;
    for (const t of (pricing.tiers || [])) { if (d >= t.min_days) { disc = t.discount; break; } }
    return { amount: +(d * rate * (1 - disc / 100)).toFixed(2), discount: disc, rate };
  };
  const est = priceFor(form.days);
  const estCost = est.amount;
  const bundleMode = bundleSlots.length >= 2;
  const bundlePct = (() => {
    if (!bundleMode || comboMode) return 0;
    const bd = pricing?.bundle_discounts || {};
    const exact = bd[bundleSlots.length] ?? bd[String(bundleSlots.length)];
    if (exact != null) return Number(exact);
    // Counts beyond the defined tiers earn the max discount (mirrors the backend).
    const vals = Object.values(bd).map(Number).filter((n) => !isNaN(n));
    return vals.length ? Math.max(...vals) : 0;
  })();
  const bundleSubtotal = bundleMode ? +bundleSlots.reduce((sum, p) => sum + (priceFor(form.days, p).amount || 0), 0).toFixed(2) : null;
  const bundleAfterBundle = bundleMode ? +(bundleSubtotal * (1 - bundlePct / 100)).toFixed(2) : null;
  const displayCost = bundleMode ? bundleAfterBundle : estCost;
  const finalCost = (displayCost != null && couponInfo?.valid)
    ? +(displayCost * (1 - couponInfo.discount / 100)).toFixed(2)
    : displayCost;
  const removeBundleSlot = (p) => setBundleSlots((s) => (s.length <= 2 ? s : s.filter((x) => x !== p)));

  if (!user) {
    return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center"><p className="text-slate-500">{tt.signin}</p></div>;
  }

  if (!eligible) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16" data-testid="advertise-gate">
        <div className="bg-white rounded-2xl border border-slate-200/80 p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#FFF4F0] flex items-center justify-center mx-auto mb-4"><Megaphone size={26} className="text-[#F5551E]" /></div>
          <h1 className="font-display font-extrabold text-2xl text-[#12234A]">{tt.gate_title}</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">{tt.gate_body_1}<strong>{tt.gate_body_strong}</strong>{tt.gate_body_2}</p>
          <div className="flex items-center justify-center gap-2 mt-5 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><BadgeCheck size={14} className="text-[#F5551E]" /> {tt.biz_account}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1"><ShieldCheck size={14} className="text-emerald-600" /> {tt.verified_seller}</span>
          </div>
          <Link to="/dashboard"><Button className="mt-6 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold" data-testid="advertise-goto-dashboard">{tt.go_dashboard}</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="font-display font-extrabold text-3xl text-[#12234A] flex items-center gap-2"><Megaphone size={26} className="text-[#F5551E]" /> {tt.header_title}</h1>
        <p className="text-sm text-slate-500 mt-1">{tt.header_sub_1}{pricing && <><strong>{tt.header_sub_price(pricing.currency, Math.min(...Object.values(pricing.rates || { d: pricing.daily_rate })))}</strong>{tt.header_sub_2}</>}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 self-start" data-testid="advertise-form">
          <h3 className="font-display font-bold text-[#12234A] mb-4">{tt.submit_h}</h3>

          <Label className="text-xs font-semibold text-slate-500">{tt.ad_image}</Label>
          <p className="text-[11px] text-slate-400 mb-1" data-testid="advertise-size-hint">{tt.recommended_for} {tt.pl_name[form.placement]}: <span className="font-semibold text-[#F5551E]">{IDEAL_SIZE[form.placement]}</span></p>
          {form.image ? (
            <div className="mt-1.5">
              <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50" style={{ aspectRatio: `${PLACEMENT_RATIO[form.placement]}` }}>
                <img src={photoUrl(form.image)} alt="" className="w-full h-full object-cover" />
                <div className="absolute top-2 right-2 flex gap-1.5">
                  {cropSrc && <button onClick={openRecrop} data-testid="advertise-recrop" title="Re-crop" className="bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70"><Crop size={14} /></button>}
                  <button onClick={() => { setForm((f) => ({ ...f, image: "" })); setCropSrc(null); }} data-testid="advertise-remove-image" title="Remove" className="bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70"><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="text-[11px] text-emerald-700 mt-1.5 flex items-center gap-1"><CheckCircle2 size={12} /> {tt.cropped_note(tt.pl_name[form.placement], IDEAL_SIZE[form.placement])}</p>
            </div>
          ) : (
            <label className="mt-1.5 flex flex-col items-center justify-center gap-1 h-36 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#F5551E] hover:bg-[#FFF9F6] transition-colors" data-testid="advertise-image-upload">
              {uploading ? <Loader2 size={20} className="animate-spin text-[#F5551E]" /> : <ImagePlus size={20} className="text-slate-400" />}
              <span className="text-xs text-slate-400">{uploading ? tt.uploading : tt.upload_creative}</span>
              <input type="file" accept="image/*" onChange={onImage} className="hidden" />
            </label>
          )}

          <div className="mt-4">
            <Label className="text-xs font-semibold text-slate-500">{tt.headline}</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={tt.headline_ph} data-testid="advertise-title-input" className="mt-1.5 rounded-xl" maxLength={80} />
          </div>

          <div className="mt-4">
            <Label className="text-xs font-semibold text-slate-500">{tt.dest_link}</Label>
            <Input value={form.link_url} onChange={(e) => setForm((f) => ({ ...f, link_url: e.target.value }))} placeholder={tt.dest_ph} data-testid="advertise-link-input" className="mt-1.5 rounded-xl" />
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="text-xs font-semibold text-slate-500">{bundleMode ? tt.bundle_label : tt.placement}</Label>
              {bundleMode ? (
                <div className="mt-1.5" data-testid="advertise-bundle-chips">
                  <div className="flex flex-wrap gap-1.5">
                    {bundleSlots.map((v) => (
                      <span key={v} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#12234A] bg-[#FFF4F0] border border-[#F5551E]/25 rounded-full pl-2.5 pr-1.5 py-1" data-testid={`advertise-bundle-chip-${v}`}>
                        {tt.pl_name[v]}{pricing?.rates ? <span className="text-[10px] text-[#F5551E]">${pricing.rates[v]}{tt.per_day}</span> : null}
                        {bundleSlots.length > 2 && <button type="button" onClick={() => removeBundleSlot(v)} title={tt.bundle_remove} data-testid={`advertise-bundle-remove-${v}`} className="ml-0.5 text-slate-400 hover:text-red-500"><X size={12} /></button>}
                      </span>
                    ))}
                  </div>
                  <p className="text-[10px] font-bold text-emerald-700 mt-1.5 inline-flex items-center gap-1" data-testid="advertise-bundle-badge"><Layers size={11} /> {comboMode ? tt.combo_badge : `${tt.bundle_badge(bundlePct)} · ${tt.bundle_hint(bundleSlots.length)}`}</p>
                </div>
              ) : (
                <>
                  <Select value={form.placement} onValueChange={(v) => setForm((f) => ({ ...f, placement: v }))}>
                    <SelectTrigger className="mt-1.5 rounded-xl" data-testid="advertise-placement-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLACEMENT_VALUES.map((v) => <SelectItem key={v} value={v} data-testid={`advertise-placement-${v}`}><span className="inline-flex items-center gap-1.5"><span>{tt.pl_labels[v]}{pricing?.rates ? ` — $${pricing.rates[v]}${tt.per_day}` : ""}</span>{v === "home_top" && <PremiumPill label={tt.premium_badge} />}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-400 mt-1.5 flex flex-wrap items-center gap-x-1 gap-y-0.5">
                    <Eye size={11} className="text-[#F5551E]" /> {tt.see_where}
                    {PLACEMENT_VALUES.map((v, i) => (
                      <span key={v}>
                        <button type="button" onClick={() => openPreview(v)} data-testid={`advertise-preview-link-${v}`} className="text-[#F5551E] font-semibold underline underline-offset-2 hover:text-[#E04812]">{tt.pl_name[v]}</button>{v === "home_top" && <PremiumPill label={tt.premium_badge} className="ml-1 align-middle" />}{i < PLACEMENT_VALUES.length - 1 ? " ·" : ""}
                      </span>
                    ))}
                  </p>
                  <Link to="/advertising/placements" data-testid="advertise-placement-guide-link" className="text-[10px] font-semibold text-[#12234A] mt-1 inline-flex items-center gap-1 hover:text-[#F5551E] transition-colors">
                    <Eye size={11} /> {tt.full_guide}
                  </Link>
                </>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500">{tt.run_len}</Label>
              <Input type="number" min={1} max={90} value={form.days} onChange={(e) => setForm((f) => ({ ...f, days: e.target.value }))} data-testid="advertise-days-input" className="mt-1.5 rounded-xl" />
              <p className="text-[10px] text-slate-400 mt-1">{tt.save_longer}</p>
            </div>
          </div>

          {!bundleMode && form.placement === "home_top" && (
            <PremiumPerks copy={tt.premium_perks} className="mt-3" />
          )}

          {!bundleMode && (form.placement === "browse" || form.placement === "browse_bottom") && (() => {
            const pct = Number(pricing?.bundle_discounts?.[2] ?? pricing?.bundle_discounts?.["2"] ?? 0);
            return (
              <div className="mt-3 rounded-xl border border-[#F5551E]/25 bg-gradient-to-br from-[#FFF4F0] to-white p-3" data-testid="fullpage-bundle-nudge">
                <p className="text-[11px] font-bold text-[#12234A] flex items-center gap-1.5"><Layers size={13} className="text-[#F5551E]" /> {tt.fullpage_title}</p>
                <p className="text-[11px] text-slate-500 mt-1">{tt.fullpage_body(pct)}</p>
                <Button type="button" size="sm" onClick={() => startBundle(["browse", "browse_bottom"])} data-testid="fullpage-bundle-btn" className="mt-2 rounded-full h-8 text-xs gap-1.5 bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">
                  <Plus size={13} /> {tt.fullpage_btn(pct)}
                </Button>
              </div>
            );
          })()}

          {!bundleMode && pricing?.bundle_discounts && (() => {
            const eligible = PLACEMENT_VALUES.filter((v) => v !== "home_top");
            const bd = pricing.bundle_discounts;
            const bdVals = Object.values(bd).map(Number).filter((n) => !isNaN(n));
            const pctFor = (n) => {
              const exact = bd[n] ?? bd[String(n)];
              if (exact != null) return Number(exact);
              return bdVals.length ? Math.max(...bdVals) : 0;
            };
            const presets = [
              { id: "browse", slots: ["browse", "browse_bottom"] },
              { id: "all", slots: eligible },
            ];
            return (
              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="bundle-presets">
                <p className="text-[11px] font-bold text-[#12234A] flex items-center gap-1.5"><Layers size={13} className="text-[#F5551E]" /> {tt.preset_title}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{tt.preset_hint}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {presets.map((p) => {
                    const pct = pctFor(p.slots.length);
                    return (
                      <button key={p.id} type="button" onClick={() => startBundle(p.slots)} data-testid={`bundle-preset-${p.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white hover:border-[#F5551E] hover:text-[#F5551E] px-3 py-1.5 text-xs font-semibold text-[#12234A] transition-colors">
                        <Plus size={13} /> {p.id === "browse" ? tt.preset_browse : tt.preset_all}
                        <span className="text-slate-400 font-normal">· {p.slots.length} slots</span>
                        {pct > 0 && <span className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5">{tt.preset_save(pct)}</span>}
                      </button>
                    );
                  })}
                  <button type="button" onClick={() => startBundle(["home_top", "home"], true)} data-testid="bundle-preset-homepage"
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-gradient-to-r from-amber-50 to-white hover:border-amber-400 px-3 py-1.5 text-xs font-semibold text-[#12234A] transition-colors">
                    <Sparkles size={13} className="text-amber-500" /> {tt.combo_name}
                    <span className="text-slate-400 font-normal">· 2 slots</span>
                    <span className="rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold px-1.5 py-0.5">{tt.combo_tag}</span>
                  </button>
                </div>
              </div>
            );
          })()}

          <div className="mt-4">
            <Label className="text-xs font-semibold text-slate-500">{tt.pref_start}</Label>
            <Input type="datetime-local" value={form.start_at} onChange={(e) => setForm((f) => ({ ...f, start_at: e.target.value }))} data-testid="advertise-start-input" className="mt-1.5 rounded-xl" />
          </div>

          {slots && !bundleMode && (
            <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-3" data-testid="advertise-slots">
              <p className="text-[11px] font-semibold text-slate-500 flex items-center gap-1 mb-2"><Activity size={13} className="text-[#F5551E]" /> {tt.slot_avail}</p>
              <div className="flex flex-wrap gap-2">
                {PLACEMENT_VALUES.map((v) => {
                  const s = slots[v] || { live_count: 0, load: "open" };
                  return (
                    <button key={v} type="button" onClick={() => setForm((f) => ({ ...f, placement: v }))} data-testid={`advertise-slot-${v}`} className={`relative text-left rounded-lg border pl-2.5 pr-7 py-1.5 transition-colors ${form.placement === v ? "border-[#F5551E] bg-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                      <span className="block text-xs font-semibold text-[#12234A]">{tt.pl_name[v]}{pricing?.rates ? <span className="ml-1 font-normal text-[10px] text-[#F5551E]">${pricing.rates[v]}{tt.per_day}</span> : null}{v === "home_top" && <PremiumPill label={tt.premium_badge} className="ml-1 align-middle" />}</span>
                      <span className={`inline-block mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${LOAD_CLS[s.load] || LOAD_CLS.open}`}>{s.live_count} {tt.live_word} · {tt.load[s.load] || tt.load.open}</span>
                      {v === "home_top" && <span className="block mt-0.5 text-[9px] font-bold text-amber-600 flex items-center gap-0.5" data-testid="advertise-slot-scarcity-home_top"><Sparkles size={9} /> {tt.scarcity}</span>}
                      <span role="button" tabIndex={0} title={tt.where_appears} data-testid={`advertise-slot-preview-${v}`} onClick={(e) => { e.stopPropagation(); openPreview(v); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); openPreview(v); } }} className="absolute top-1 right-1 p-1 rounded-md text-slate-400 hover:text-[#F5551E] hover:bg-[#FFF4F0]"><Eye size={13} /></span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-4">
            <Label className="text-xs font-semibold text-slate-500">{tt.promo}</Label>
            <div className="flex gap-2 mt-1.5">
              <div className="relative flex-1">
                <Ticket size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={coupon} onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponInfo(null); }} placeholder={tt.promo_ph} data-testid="advertise-coupon-input" className="rounded-xl pl-9 uppercase" />
              </div>
              <Button variant="outline" onClick={applyCoupon} disabled={checkingCoupon || !coupon.trim()} data-testid="advertise-coupon-apply" className="rounded-xl shrink-0">{checkingCoupon ? <Loader2 size={15} className="animate-spin" /> : tt.apply}</Button>
            </div>
            {couponInfo?.valid && <p className="text-[11px] text-emerald-600 font-semibold mt-1.5" data-testid="advertise-coupon-applied">{tt.coupon_applied(couponInfo.code, couponInfo.discount)}</p>}
          </div>

          {pricing && displayCost != null && (
            <div className="mt-4 rounded-xl bg-[#FFF4F0] border border-[#F5551E]/20 p-3 flex items-center justify-between" data-testid="advertise-cost">
              <span className="text-sm text-slate-600 flex items-center gap-2">
                {tt.est_cost}
                {bundleMode && comboMode && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5" data-testid="advertise-combo-badge">{tt.combo_badge}</span>}
                {bundleMode && !comboMode && <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5" data-testid="advertise-bundle-discount-badge">{tt.bundle_badge(bundlePct)} −{bundlePct}%</span>}
                {!bundleMode && est.discount > 0 && <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5" data-testid="advertise-discount-badge">{est.discount}% {tt.off}</span>}
                {couponInfo?.valid && <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">+{couponInfo.discount}% {tt.code_suffix}</span>}
              </span>
              <span className="font-display font-bold text-lg text-[#F5551E]">
                {bundleMode && bundlePct > 0 && <span className="text-sm text-slate-400 line-through mr-2 font-normal">${bundleSubtotal.toFixed(2)}</span>}
                {!bundleMode && couponInfo?.valid && <span className="text-sm text-slate-400 line-through mr-2 font-normal">${displayCost.toFixed(2)}</span>}
                {pricing.currency} ${finalCost.toFixed(2)}
              </span>
            </div>
          )}

          <Button onClick={submit} disabled={submitting || !form.image || !form.link_url.trim()} data-testid="advertise-submit-btn" className="mt-5 w-full rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2 disabled:opacity-40">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Megaphone size={16} />} {bundleMode ? tt.submit_bundle : tt.submit_btn}
          </Button>
          <p className="text-[11px] text-slate-400 mt-2 text-center">{tt.submit_note}</p>
        </div>

        <div className="space-y-5">
          {receipt && (
            <div className="bg-white rounded-2xl border border-emerald-200 p-5" data-testid="advertise-receipt">
              <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2 mb-2"><CheckCircle2 size={18} className="text-emerald-600" /> {tt.receipt_h}</h3>
              <p className="text-sm text-slate-600">{tt.receipt_body_1}<strong>{tt.receipt_body_strong}</strong>{tt.receipt_body_2}</p>
              <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 divide-y divide-slate-100 text-sm">
                <div className="flex items-center justify-between p-3"><span className="text-slate-500 inline-flex items-center gap-1"><Mail size={14} /> {tt.send_to}</span><span className="font-semibold text-[#12234A]">{receipt.payment_email}</span></div>
                <div className="flex items-center justify-between p-3"><span className="text-slate-500">{tt.amount}</span><span className="font-semibold text-[#F5551E]">{receipt.currency} ${receipt.amount}</span></div>
                <div className="flex items-center justify-between p-3"><span className="text-slate-500">{tt.msg_ref}</span><span className="font-mono font-bold text-[#12234A]" data-testid="advertise-payment-ref">{receipt.payment_ref}</span></div>
                <div className="flex items-center justify-between p-3"><span className="text-slate-500 inline-flex items-center gap-1"><Calendar size={14} /> {tt.runs}</span><span className="text-[#12234A]">{fmtDate(receipt.start_at)} – {fmtDate(receipt.end_at)} ({receipt.days}d)</span></div>
                {receipt.bundle && (
                  <div className="flex items-center justify-between p-3" data-testid="advertise-receipt-placements"><span className="text-slate-500 inline-flex items-center gap-1"><Layers size={14} /> {tt.receipt_placements}</span><span className="text-[#12234A] text-right">{receipt.placements.map((p) => tt.pl_name[p]).join(" · ")} <span className="text-[10px] font-bold text-emerald-700">({tt.bundle_badge(receipt.bundle_discount)})</span></span></div>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">{tt.include_ref_1}<strong>{receipt.payment_ref}</strong>{tt.include_ref_2}</p>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200/80 p-5" data-testid="advertise-my-ads">
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
              <h3 className="font-display font-bold text-[#12234A]">{tt.my_ads}</h3>
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="advertise-digest-toggle-wrap">
                <BellRing size={14} className={digestOn ? "text-[#F5551E]" : "text-slate-400"} />
                <span>{tt.weekly_email}</span>
                <Switch checked={digestOn} onCheckedChange={toggleDigest} data-testid="advertise-digest-toggle" />
              </label>
            </div>
            {!myAds ? (
              <div className="space-y-2">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
            ) : myAds.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">{tt.no_ads}</p>
            ) : (
              <div className="space-y-3 max-h-[520px] overflow-y-auto">
                {myAds.map((ad) => {
                  return (
                    <div key={ad.id} className="border border-slate-100 rounded-xl p-3 flex gap-3" data-testid={`my-ad-${ad.id}`}>
                      <img src={photoUrl(ad.image)} alt="" className="h-16 w-24 rounded-lg object-cover bg-slate-100 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[#12234A] truncate">{ad.title || tt.untitled}</p>
                            <p className="text-[11px] text-slate-500"><PlacementLink placement={ad.placement} label={tt.pl_name[ad.placement]} className="text-slate-500" /> · {fmtDate(ad.start_at)}–{fmtDate(ad.end_at)}</p>
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${STATUS_CLS[ad.status] || STATUS_CLS.pending}`} data-testid={`my-ad-status-${ad.id}`}>{tt.status[ad.status] || tt.status.pending}</span>
                        </div>
                        <div className="flex items-center gap-4 mt-1.5 text-[11px] text-slate-500">
                          <span className="inline-flex items-center gap-1"><Eye size={12} className="text-slate-400" /> {(ad.impressions || 0).toLocaleString()}</span>
                          <span className="inline-flex items-center gap-1"><MousePointerClick size={12} className="text-slate-400" /> {(ad.clicks || 0).toLocaleString()}</span>
                          <span className="font-semibold text-[#12234A]">{ad.ctr != null ? `${ad.ctr}% CTR` : "—"}</span>
                        </div>
                        {ad.payment_status === "awaiting_payment" && (
                          <p className="text-[11px] text-amber-600 mt-1.5 inline-flex items-center gap-1"><Mail size={12} /> e-Transfer {ad.currency} ${ad.amount} · ref {ad.payment_ref}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          <button onClick={() => downloadReport(ad)} data-testid={`my-ad-report-${ad.id}`} className="text-[11px] font-semibold text-[#F5551E] inline-flex items-center gap-1 hover:underline"><Download size={12} /> {tt.download_report}</button>
                          {["expired", "live", "paused"].includes(ad.status) && (
                            <button onClick={() => { setRenewAd(ad); setRenewDays(ad.days || 7); }} data-testid={`my-ad-renew-${ad.id}`} className="text-[11px] font-semibold text-[#12234A] inline-flex items-center gap-1 hover:underline"><RefreshCw size={12} /> {ad.status === "expired" ? tt.rerun : tt.extend}</button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showCropper} onOpenChange={(o) => !o && setShowCropper(false)}>
        <DialogContent className="max-w-lg" data-testid="cropper-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Crop size={18} className="text-[#F5551E]" /> {tt.crop_for(tt.pl_name[cropPlacement], IDEAL_SIZE[cropPlacement])}</DialogTitle>
            <DialogDescription>{tt.crop_desc}</DialogDescription>
          </DialogHeader>
          <div className="relative w-full bg-slate-900 rounded-xl overflow-hidden" style={{ height: 280 }}>
            {cropSrc && (
              <Cropper image={cropSrc} crop={crop} zoom={zoom} aspect={PLACEMENT_RATIO[cropPlacement]}
                onCropChange={setCrop} onZoomChange={setZoom}
                onCropComplete={(_, px) => setCroppedAreaPixels(px)} objectFit="contain" />
            )}
          </div>
          <div className="flex items-center gap-3 px-1">
            <ZoomOut size={16} className="text-slate-400 shrink-0" />
            <Slider min={1} max={3} step={0.01} value={[zoom]} onValueChange={(v) => setZoom(v[0])} data-testid="cropper-zoom-slider" className="flex-1" />
            <ZoomIn size={16} className="text-slate-400 shrink-0" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCropper(false)} className="rounded-xl">{tt.cancel}</Button>
            <Button onClick={applyCrop} disabled={uploading} data-testid="cropper-apply-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-1">{uploading ? <Loader2 size={15} className="animate-spin" /> : <Crop size={15} />} {tt.apply_crop}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!previewPlacement} onOpenChange={(o) => !o && setPreviewPlacement(null)}>
        <DialogContent className="max-w-2xl" data-testid="placement-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye size={18} className="text-[#F5551E]" /> {previewPlacement && tt.preview[previewPlacement].title}{previewPlacement === "home_top" && <PremiumPill label={tt.premium_badge} />}</DialogTitle>
            <DialogDescription>{previewPlacement && tt.preview[previewPlacement].blurb}</DialogDescription>
          </DialogHeader>
          {previewPlacement && (
            <div>
              <div className="flex items-center gap-1 p-1 mb-2 rounded-xl bg-slate-100 w-fit" data-testid="preview-view-toggle">
                <button type="button" onClick={() => setShowMine(false)} data-testid="preview-toggle-location" className={`text-[11px] font-semibold px-3 py-1 rounded-lg transition-colors ${!showMine ? "bg-white text-[#12234A] shadow-sm" : "text-slate-500"}`}>{tt.where_appears}</button>
                <button type="button" onClick={() => setShowMine(true)} data-testid="preview-toggle-mine" className={`text-[11px] font-semibold px-3 py-1 rounded-lg transition-colors ${showMine ? "bg-white text-[#F5551E] shadow-sm" : "text-slate-500"}`}>{tt.my_banner}</button>
              </div>
              {showMine ? (
                <div data-testid="preview-mockup-wrap">
                  <BannerMockup placement={previewPlacement} image={form.image ? photoUrl(form.image) : ""} onDims={setBannerDims} />
                  {!form.image ? (
                    <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1"><ImagePlus size={12} className="text-[#F5551E]" /> {tt.upload_above}</p>
                  ) : (() => {
                    const target = PLACEMENT_RATIO[previewPlacement];
                    const willCrop = bannerDims ? Math.abs(bannerDims.w / bannerDims.h - target) / target > 0.08 : false;
                    return willCrop ? (
                      <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 flex items-start gap-1.5" data-testid="mockup-crop-warning">
                        <AlertTriangle size={13} className="text-amber-600 mt-0.5 shrink-0" />
                        <p className="text-[11px] text-amber-800">{tt.crop_warn_1}<strong>{bannerDims.w}×{bannerDims.h}</strong>{tt.crop_warn_2}<strong>{IDEAL_SIZE[previewPlacement]}</strong>{tt.crop_warn_3}</p>
                      </div>
                    ) : (
                      <p className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 text-[11px] text-emerald-800 flex items-center gap-1.5" data-testid="mockup-fit-ok"><CheckCircle2 size={13} className="text-emerald-600 shrink-0" /> {tt.fit_ok(IDEAL_SIZE[previewPlacement])}</p>
                    );
                  })()}
                </div>
              ) : (
                <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                  <img src={PLACEMENT_PREVIEW_IMG[previewPlacement]} alt={`${tt.preview[previewPlacement].title} preview`} className="w-full h-auto block" data-testid="placement-preview-image" />
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2 justify-between">
                <p className="text-[11px] text-slate-500">{tt.rec_size} <span className="font-semibold text-[#F5551E]">{IDEAL_SIZE[previewPlacement]}</span></p>
                {pricing && (
                  <div className="rounded-xl bg-[#FFF4F0] border border-[#F5551E]/20 px-3 py-1.5" data-testid="placement-preview-price">
                    <span className="text-[11px] text-slate-600">{tt.price_word}</span>
                    <span className="font-display font-bold text-sm text-[#F5551E]">{pricing.currency} ${priceFor(form.days, previewPlacement).rate}{tt.per_day}</span>
                    <span className="text-[11px] text-slate-500">{tt.per_day_for(pricing.currency, (priceFor(form.days, previewPlacement).amount || 0).toFixed(2), Number(form.days) || 7)}</span>
                    {priceFor(form.days, previewPlacement).discount > 0 && <span className="ml-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-full px-1.5 py-0.5">{priceFor(form.days, previewPlacement).discount}% {tt.off}</span>}
                  </div>
                )}
              </div>
              {previewPlacement === "home_top" && (
                <PremiumPerks copy={tt.premium_perks} className="mt-3" />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewPlacement(null)} className="rounded-xl">{tt.close}</Button>
            {previewPlacement && (
              <Button onClick={() => { trackPlacement(previewPlacement, "pick"); setForm((f) => ({ ...f, placement: previewPlacement })); setPreviewPlacement(null); }} data-testid="placement-preview-choose-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">{tt.choose_placement}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renewAd} onOpenChange={(o) => !o && setRenewAd(null)}>
        <DialogContent data-testid="renew-dialog">
          <DialogHeader>
            <DialogTitle>{renewAd?.status === "expired" ? tt.rerun_campaign : tt.extend_campaign}</DialogTitle>
            <DialogDescription>{tt.renew_desc(renewAd?.title || tt.untitled)}</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-semibold text-slate-500">{tt.run_len}</Label>
            <Input type="number" min={1} max={90} value={renewDays} onChange={(e) => setRenewDays(e.target.value)} data-testid="renew-days-input" className="mt-1.5 rounded-xl" />
            {pricing && (
              <div className="mt-3 rounded-xl bg-[#FFF4F0] border border-[#F5551E]/20 p-3 flex items-center justify-between">
                <span className="text-sm text-slate-600 flex items-center gap-2">{tt.cost}{priceFor(renewDays, renewAd?.placement).discount > 0 && <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">{priceFor(renewDays, renewAd?.placement).discount}% {tt.off}</span>}</span>
                <span className="font-display font-bold text-lg text-[#F5551E]">{pricing.currency} ${(priceFor(renewDays, renewAd?.placement).amount || 0).toFixed(2)}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewAd(null)} className="rounded-xl">{tt.cancel}</Button>
            <Button onClick={doRenew} disabled={renewing} data-testid="renew-confirm-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
              {renewing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} {tt.book_renewal}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
